import apiService from '../apiService';

/**
 * 取得訂單列表
 * @param {Object} params - 查詢參數
 * @param {string} params.status - 訂單狀態（pending, paid, completed, cancelled, refunding, refunded, partially_refunded）
 * @param {number} params.user_id - 用戶 ID
 * @param {string} params.order_no - 訂單編號
 * @param {string} params.date_from - 開始日期
 * @param {string} params.date_to - 結束日期
 * @param {number} params.min_amount - 最小金額
 * @param {number} params.max_amount - 最大金額
 * @param {number} params.per_page - 每頁筆數（預設 15）
 * @param {number} params.page - 頁碼
 * @param {string} params.sort_by - 排序欄位（grand_total | placed_at | paid_at | order_no | status | payment_method | payment_account_key | user_name；不帶則預設依建立時間）
 * @param {string} params.sort_order - 排序方向（asc | desc）
 * @returns {Promise} API 響應
 */
export const getShopOrders = async (params = {}) => {
    const url = '/api/admin/shop-orders';
    return apiService.GET(url, params);
};

/**
 * 取得單個訂單詳細資料
 * @param {number} id - 訂單ID
 * @returns {Promise} API 響應
 */
export const getShopOrder = async (id) => {
    const url = `/api/admin/shop-orders/${id}`;
    return apiService.GET(url);
};

/**
 * 取得訂單狀態歷史
 * @param {number} id - 訂單ID
 * @returns {Promise} API 響應
 */
export const getShopOrderHistory = async (id) => {
    const url = `/api/admin/shop-orders/${id}/history`;
    return apiService.GET(url);
};

/**
 * 更新訂單狀態
 * @param {number} id - 訂單ID
 * @param {Object} data - 訂單狀態資料
 * @param {string} data.status - 新狀態
 * @returns {Promise} API 響應
 */
export const updateShopOrderStatus = async (id, data) => {
    const url = `/api/admin/shop-orders/${id}/status`;
    return apiService.PUT(url, data);
};

/**
 * 標記訂單為已付款
 * @param {number} id - 訂單ID
 * @returns {Promise} API 響應
 */
export const markOrderPaid = async (id) => {
    const url = `/api/admin/shop-orders/${id}/mark-paid`;
    return apiService.POST(url);
};

/**
 * 標記訂單為已完成
 * @param {number} id - 訂單ID
 * @returns {Promise} API 響應
 */
export const markOrderCompleted = async (id) => {
    const url = `/api/admin/shop-orders/${id}/mark-completed`;
    return apiService.POST(url);
};

/**
 * 取消訂單（管理員）
 * @param {number} id - 訂單ID
 * @returns {Promise} API 響應
 */
export const cancelOrder = async (id) => {
    const url = `/api/admin/shop-orders/${id}/cancel`;
    return apiService.POST(url);
};

/**
 * 出貨
 * @param {number} id - 訂單ID
 * @returns {Promise} API 響應
 */
export const shipOrder = async (id) => {
    const url = `/api/admin/shop-orders/${id}/ship`;
    return apiService.POST(url);
};

/**
 * 完成訂單
 * @param {number} id - 訂單ID
 * @returns {Promise} API 響應
 */
export const completeOrder = async (id) => {
    const url = `/api/admin/shop-orders/${id}/complete`;
    return apiService.POST(url);
};

/**
 * 退款
 * @param {number} id - 訂單ID
 * @param {Object} data - 退款參數（fee_amount, fee_note, reason, refund_amount）
 * @returns {Promise} API 響應
 */
export const refundOrder = async (id, data = {}) => {
    const url = `/api/admin/shop-orders/${id}/refund`;
    return apiService.POST(url, data);
};

/**
 * 取得支付交易記錄（第三方支付紀錄）
 * @param {number} id - 支付ID (payment_id)
 * @returns {Promise} API 響應
 */
export const getPaymentTransactions = async (id) => {
    const url = `/api/admin/shop-payments/${id}/transactions`;
    return apiService.GET(url);
};

/**
 * 查詢訂單金流狀態
 * @param {number} id - 訂單ID
 * @returns {Promise} API 響應
 */
export const getPaymentStatus = async (id) => {
    const url = `/api/admin/shop-orders/${id}/payment-status`;
    // 審核時探測金流：未付款／查無交易為預期情境，勿跳全域 400 toast
    return apiService.GET(url, {}, { skipNotification: true });
};

/**
 * 取消信用卡授權
 * @param {number} id - 訂單ID
 * @returns {Promise} API 響應
 */
export const cancelAuth = async (id) => {
    const url = `/api/admin/shop-orders/${id}/cancel-auth`;
    return apiService.POST(url);
};

/**
 * 匯出訂單收款列表 Excel
 * @param {Object} params - 篩選參數
 * @returns {Promise} API 響應 (Blob)
 */
export const exportShopOrders = async (params = {}) => {
    const url = '/api/admin/shop-orders/export';
    return apiService.GET(url, params, { responseType: 'blob' });
};

/**
 * 編輯匯款資料（銀行轉帳）
 * @param {number} id - 訂單ID
 * @param {{ transfer_date?: string|null, transfer_bank?: string|null, account_name?: string|null, account_last_5?: string|null }} data
 * @returns {Promise} API 響應
 *
 * 後端說明：
 * - Method: POST
 * - URL:   /api/admin/shop-orders/{id}/remittance
 * - 四個欄位皆為選填，沒帶的欄位保持原值
 */
export const updateBankTransferInfo = async (id, data) => {
    const url = `/api/admin/shop-orders/${id}/remittance`;
    return apiService.POST(url, data);
};

/**
 * 編輯發票資訊（統一編號、載具/捐贈碼）
 * @param {number} id - 訂單ID
 * @param {{ vat_number?: string|null, invoice_title?: string|null, carrier_code?: string|null }} data
 * @returns {Promise} API 響應
 *
 * 後端說明：
 * - Method: PUT
 * - URL:   /api/admin/shop-orders/{id}/invoice
 */
export const updateOrderInvoiceInfo = async (id, data) => {
    const url = `/api/admin/shop-orders/${id}/invoice`;
    return apiService.PUT(url, data);
};

/**
 * 發票分組列表（財務對帳）
 */
export const getShopOrderInvoiceGroups = async (params = {}) => {
    return apiService.GET('/api/admin/shop-order-invoice-groups', params);
};

/**
 * 更新發票分組（Ragic 回寫）
 */
export const updateShopOrderInvoiceGroup = async (id, data) => {
    return apiService.PUT(`/api/admin/shop-order-invoice-groups/${id}`, data);
};

/**
 * 匯出發票分組列表 Excel
 */
export const exportShopOrderInvoiceGroups = async (params = {}) => {
    return apiService.GET('/api/admin/shop-order-invoice-groups/export', params, {
        responseType: 'blob'
    });
};
